import { useEffect, useMemo, useState } from "react";
import { SignUpButton, useUser } from "@clerk/clerk-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RankBadge from "../../components/club/RankBadge";
import { ClubApi } from "../../services/clubApi";
import { useAuthStatus } from "../../components/auth/AuthProvider";
import {
  CLUB_JOIN_OPTIONS,
  ClubJoinOption,
  launchClubCheckout,
} from "../../utils/clubJoin";
import { devError, devLog } from "../../utils/devLog";

const upsertNameMeta = (name: string, content: string) => {
  if (!name || !content) return;
  let node = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute("name", name);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
};

const upsertPropertyMeta = (property: string, content: string) => {
  if (!property || !content) return;
  let node = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute("property", property);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
};

const AGENCY_ANONYMOUS_VISITOR_STORAGE_KEY = "agency_conversion_anonymous_id";
const AGENCY_LANDING_VIEW_SESSION_KEY = "agency_conversion_landing_view_recorded";
const AGENCY_TRIAL_AUTOCLAIM_INFLIGHT_KEY_PREFIX = "agency_trial_autoclaim_inflight_";
const AGENCY_RANK_TEASER = [
  {
    key: "junior_detective",
    label: "Junior Detective",
    status: "Entry clearance",
    revealed: true,
  },
  {
    key: "detective",
    label: "Detective",
    status: "Clearance rises through solved cases",
    revealed: true,
  },
  {
    key: "senior_detective",
    label: "Classified",
    status: "Higher bureau access awaits",
    revealed: false,
  },
  {
    key: "lead_detective",
    label: "Classified",
    status: "Promotion tier withheld",
    revealed: false,
  },
  {
    key: "special_investigator",
    label: "Classified",
    status: "Final clearance undisclosed",
    revealed: false,
  },
];

const getAgencyAnonymousId = () => {
  if (typeof window === "undefined") return "";
  const existing = window.localStorage.getItem(AGENCY_ANONYMOUS_VISITOR_STORAGE_KEY);
  if (existing) return existing;
  const generated = `agency_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(AGENCY_ANONYMOUS_VISITOR_STORAGE_KEY, generated);
  return generated;
};

const getAgencyUserDisplayName = (user: any) => {
  const fullName = String(user?.fullName || "").trim();
  if (fullName) return fullName;

  return `${String(user?.firstName || "").trim()} ${String(user?.lastName || "").trim()}`.trim();
};

const ClubWelcomePage = ({
  onMembershipCreated,
  showTrialReminderBanner = false,
  trialBannerDebug = null,
}: {
  onMembershipCreated: () => void;
  showTrialReminderBanner?: boolean;
  trialBannerDebug?: {
    memberCreatedAt: string;
    isCreatedToday: boolean;
    isNonMember: boolean;
  } | null;
}) => {
  const { user, isSignedIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuthStatus();
  const [workingOption, setWorkingOption] = useState("");
  const [sendingFreeCase, setSendingFreeCase] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [freeCaseResult, setFreeCaseResult] = useState("");
  const [error, setError] = useState("");
  const [hasRequestedTrialKey, setHasRequestedTrialKey] = useState(false);
  const agencyLogoSrc = "/images/agency_logo.png";
  const agencyOgImageSrc = "/agency-assets/og-image.png";
  const anonymousId = useMemo(() => getAgencyAnonymousId(), []);

  const trialRequestStorageKey = useMemo(() => {
    if (!isSignedIn || !user?.id) return "";
    return `club_trial_key_requested_${user.id}`;
  }, [isSignedIn, user?.id]);

  const digitalOption = useMemo<ClubJoinOption | null>(
    () => CLUB_JOIN_OPTIONS.find((option) => option.id === "digital") || null,
    []
  );

  const hasRequiredCheckoutNameFields = Boolean(
    String(user?.firstName || "").trim() && String(user?.lastName || "").trim()
  );
  const freeCasePayload = useMemo(
    () => ({
      email: user?.primaryEmailAddress?.emailAddress || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      displayName: getAgencyUserDisplayName(user),
    }),
    [user?.firstName, user?.id, user?.lastName, user?.primaryEmailAddress?.emailAddress]
  );
  const hasRequiredTrialIdentity = Boolean(
    String(freeCasePayload.firstName || "").trim() || String(freeCasePayload.displayName || "").trim()
  );
  const trialAutoClaimInflightKey = useMemo(() => {
    if (!isSignedIn || !user?.id) return "";
    return `${AGENCY_TRIAL_AUTOCLAIM_INFLIGHT_KEY_PREFIX}${user.id}`;
  }, [isSignedIn, user?.id]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Cold Case Agency by Cold Case Game Kits";

    upsertNameMeta(
      "description",
      "Join Cold Case Agency: start with a free case, unlock a new monthly case file, progress through Shroud of Darkness, and rise in rank as you solve."
    );
    upsertPropertyMeta("og:title", "Cold Case Agency by Cold Case Game Kits");
    upsertPropertyMeta(
      "og:description",
      "Start with a free case, then continue with monthly case files, weekly Shroud of Darkness progression, and rank advancement inside Cold Case Agency."
    );
    upsertPropertyMeta("og:type", "website");
    upsertPropertyMeta("og:image", agencyOgImageSrc);
    upsertNameMeta("twitter:card", "summary_large_image");
    upsertNameMeta("twitter:title", "Cold Case Agency by Cold Case Game Kits");
    upsertNameMeta(
      "twitter:description",
      "Claim a free case, then unlock monthly case files, weekly archive progression, and earned rank inside Cold Case Agency."
    );
    upsertNameMeta("twitter:image", agencyOgImageSrc);

    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!user || !isSignedIn) return;
    ClubApi.bootstrapMe({
      ...freeCasePayload,
    }).catch((err) => {
      devError("agency_welcome", "bootstrap_failed", {
        path: "/agency",
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
    });
  }, [freeCasePayload, isSignedIn, user?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!anonymousId) return;
    if (window.sessionStorage.getItem(AGENCY_LANDING_VIEW_SESSION_KEY) === "true") return;

    const occurredAt = new Date().toISOString();
    devLog("conversion", "landing_view_track_requested", {
      path: "/agency",
      anonymousId,
      occurredAt,
      isSignedIn,
    });

    ClubApi.trackAgencyConversionEvent({
      eventType: "agency_landing_view",
      anonymousId,
      sourcePath: "/agency",
      occurredAt,
      meta: {
        isSignedIn: Boolean(isSignedIn),
      },
    })
      .then(() => {
        window.sessionStorage.setItem(AGENCY_LANDING_VIEW_SESSION_KEY, "true");
        devLog("conversion", "landing_view_track_finished", {
          path: "/agency",
          anonymousId,
        });
      })
      .catch((err) => {
        devError("conversion", "landing_view_track_failed", {
          path: "/agency",
          anonymousId,
          status: err?.response?.status,
          message: err?.message,
        });
      });
  }, [anonymousId, isSignedIn]);

  useEffect(() => {
    if (!trialRequestStorageKey) {
      setHasRequestedTrialKey(false);
      return;
    }
    setHasRequestedTrialKey(window.localStorage.getItem(trialRequestStorageKey) === "true");
  }, [trialRequestStorageKey]);

  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      setHasRequestedTrialKey(false);
      return;
    }

    let cancelled = false;

    devLog("api", "agency_trial_key_state_sync_started", {
      route: "/agency",
      userId: String(user.id),
      hasStorageKey: Boolean(trialRequestStorageKey),
    });

    ClubApi.getMe()
      .then((response) => {
        if (cancelled) return;
        const trialRequestCount = Number(response?.member?.trialRequestCount || 0);
        const hasServerTrialRequest =
          Boolean(response?.member?.trialRequestedAt) || trialRequestCount > 0;

        if (trialRequestStorageKey) {
          if (hasServerTrialRequest) {
            window.localStorage.setItem(trialRequestStorageKey, "true");
          } else {
            window.localStorage.removeItem(trialRequestStorageKey);
          }
        }

        setHasRequestedTrialKey(hasServerTrialRequest);
        devLog("api", "agency_trial_key_state_reconciled", {
          route: "/agency",
          userId: String(user.id),
          hasStorageKey: Boolean(trialRequestStorageKey),
          hasServerTrialRequest,
          trialRequestCount,
        });
      })
      .catch((err: any) => {
        if (cancelled) return;
        devError("api", "agency_trial_key_state_sync_failed", {
          route: "/agency",
          userId: String(user.id),
          hasStorageKey: Boolean(trialRequestStorageKey),
          status: err?.response?.status,
          message: String(err?.message || ""),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, trialRequestStorageKey, user?.id]);

  const startJoin = async (option: ClubJoinOption) => {
    if (!isSignedIn || !user) return;
    if (!hasRequiredCheckoutNameFields) {
      setError("First and last name are required in your Clerk profile before joining.");
      return;
    }
    try {
      setError("");
      setWorkingOption(option.id);

      await ClubApi.bootstrapMe({
        ...freeCasePayload,
      });

      onMembershipCreated();

      const checkout = launchClubCheckout(option);
      if (!checkout.launched) {
        devError("conversion", "agency_checkout_launch_unavailable", {
          path: "/agency",
          optionId: option.id,
          productPath: option.productPath,
          checkoutUrlConfigured: Boolean(option.checkoutUrl),
          method: checkout.method,
        });
        setError("Checkout is not available yet. Please contact support.");
      } else if (anonymousId) {
        devLog("conversion", "agency_checkout_launch_succeeded", {
          path: "/agency",
          optionId: option.id,
          productPath: option.productPath,
          checkoutUrlConfigured: Boolean(option.checkoutUrl),
          method: checkout.method,
        });
        const occurredAt = new Date().toISOString();
        devLog("conversion", "checkout_started_track_requested", {
          path: "/agency",
          anonymousId,
          optionId: option.id,
          method: checkout.method,
          occurredAt,
        });
        ClubApi.trackAgencyConversionEvent({
          eventType: "checkout_started",
          anonymousId,
          sourcePath: "/agency",
          occurredAt,
          meta: {
            optionId: option.id,
            checkoutMethod: checkout.method,
            productPath: option.productPath,
          },
        })
          .then(() => {
            devLog("conversion", "checkout_started_track_finished", {
              path: "/agency",
              anonymousId,
              optionId: option.id,
            });
          })
          .catch((trackError) => {
            devError("conversion", "checkout_started_track_failed", {
              path: "/agency",
              anonymousId,
              optionId: option.id,
              status: trackError?.response?.status,
              message: trackError?.message,
            });
          });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "We couldn't start your membership setup.");
    } finally {
      setWorkingOption("");
    }
  };

  const requestFreeCase = async ({
    trigger = "manual",
    suppressSuccessMessage = false,
  }: {
    trigger?: "manual" | "auto";
    suppressSuccessMessage?: boolean;
  } = {}) => {
    if (!isSignedIn || !user) return;
    if (!hasRequiredTrialIdentity) {
      setError("Your Clerk profile needs a first name or display name before trial access can be prepared.");
      return;
    }
    const logEventPrefix =
      trigger === "auto" ? "agency_trial_key_autoclaim" : "agency_trial_key_request";
    try {
      setError("");
      if (!suppressSuccessMessage) setFreeCaseResult("");
      setSendingFreeCase(true);
      devLog("api", `${logEventPrefix}_started`, {
        route: location.pathname,
        userId: String(user.id),
        email: String(freeCasePayload.email || ""),
        hasFirstName: Boolean(String(freeCasePayload.firstName || "").trim()),
        hasLastName: Boolean(String(freeCasePayload.lastName || "").trim()),
        hasDisplayName: Boolean(String(freeCasePayload.displayName || "").trim()),
      });

      await ClubApi.bootstrapMe({
        ...freeCasePayload,
      });

      const response = await ClubApi.requestFreeDigitalCase({
        ...freeCasePayload,
      });

      if (trialRequestStorageKey) {
        window.localStorage.setItem(trialRequestStorageKey, "true");
      }
      setHasRequestedTrialKey(true);

      if (!suppressSuccessMessage) {
        if (response?.sent) {
          setFreeCaseResult(
            `Email sent successfully (${response?.provider || "provider"}, id: ${response?.id || "n/a"}).`
          );
        } else if (response?.skipped) {
          setFreeCaseResult(`Email skipped: ${response?.reason || "missing configuration"}.`);
        } else {
          setFreeCaseResult(
            `Email send failed${response?.status ? ` (status ${response.status})` : ""}.`
          );
        }
      }
      devLog("api", `${logEventPrefix}_succeeded`, {
        route: location.pathname,
        userId: String(user.id),
        queued: Boolean(response?.queued),
        sent: Boolean(response?.sent),
        skipped: Boolean(response?.skipped),
        provider: String(response?.provider || ""),
        reason: String(response?.reason || ""),
        status: Number(response?.status || 0),
      });
      onMembershipCreated();
      if (location.pathname !== "/agency/dashboard") {
        navigate("/agency/dashboard", { replace: true });
      }
    } catch (err: any) {
      if (trialRequestStorageKey) {
        window.localStorage.removeItem(trialRequestStorageKey);
      }
      setHasRequestedTrialKey(false);
      devError("api", `${logEventPrefix}_failed`, {
        route: location.pathname,
        userId: String(user.id),
        status: err?.response?.status,
        message: String(err?.message || ""),
        response: err?.response?.data || null,
      });
      setError(
        err?.response?.data?.details ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to send your free case email right now."
      );
    } finally {
      setSendingFreeCase(false);
    }
  };

  useEffect(() => {
    if (!isSignedIn || !user?.id || !trialRequestStorageKey || !trialAutoClaimInflightKey) return;
    if (!hasRequiredTrialIdentity || hasRequestedTrialKey) return;
    if (location.pathname !== "/agency/dashboard") return;

    if (window.sessionStorage.getItem(trialAutoClaimInflightKey) === "true") {
      devLog("api", "agency_trial_key_autoclaim_already_inflight", {
        route: location.pathname,
        userId: String(user.id),
      });
      return;
    }

    window.sessionStorage.setItem(trialAutoClaimInflightKey, "true");
    devLog("api", "agency_trial_key_autoclaim_queued", {
      route: location.pathname,
      userId: String(user.id),
      storageKey: trialRequestStorageKey,
    });

    void requestFreeCase({ trigger: "auto", suppressSuccessMessage: true }).finally(() => {
      window.sessionStorage.removeItem(trialAutoClaimInflightKey);
    });
  }, [
    hasRequestedTrialKey,
    hasRequiredTrialIdentity,
    isSignedIn,
    location.pathname,
    trialAutoClaimInflightKey,
    trialRequestStorageKey,
    user?.id,
  ]);

  const testEmailSetup = async () => {
    if (!isSignedIn || !user) return;
    try {
      setError("");
      setFreeCaseResult("");
      setTestingEmail(true);

      const response = await ClubApi.testFreeDigitalCaseEmail({
        email: user?.primaryEmailAddress?.emailAddress || "",
      });

      setFreeCaseResult(
        `Email setup test: ${response?.readyToSend ? "READY" : "NOT READY"} | key=${response?.checks?.hasResendKey ? "yes" : "no"}, from=${response?.checks?.hasFrom ? "yes" : "no"}, replyTo=${response?.checks?.hasReplyTo ? "yes" : "no"}`
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to test email setup right now.");
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <section className="min-h-screen w-full bg-[#05070c] text-[#eceae4]">
      <div className="mx-auto max-w-[1280px] px-4 pb-16 pt-4 sm:px-6 sm:pb-20 sm:pt-5 lg:px-8">
        {showTrialReminderBanner && (
          <div className="rounded-xl border border-emerald-300/35 bg-emerald-500/12 p-4">
            <p className="text-sm font-semibold text-emerald-100">
              Check your email for your free case access key.
            </p>
            {isAdmin && trialBannerDebug && (
              <p className="mt-2 font-mono text-xs text-emerald-100/85">
                debug: nonMember={String(trialBannerDebug.isNonMember)} | createdToday=
                {String(trialBannerDebug.isCreatedToday)} | createdAt=
                {trialBannerDebug.memberCreatedAt || "n/a"}
              </p>
            )}
          </div>
        )}

        {!isSignedIn && (
          <div className="mt-4 text-sm text-[#b6b1a3]">
            Already a member?{" "}
            <Link
              to="/agency/sign-in"
              className="font-semibold text-[#eceae4] underline decoration-[#6d6758] underline-offset-4 hover:text-[#f3e7c8]"
            >
              Sign in to continue
            </Link>
          </div>
        )}

        {isSignedIn && !hasRequiredTrialIdentity && (
          <div className="mt-4 rounded-xl border border-amber-300/35 bg-amber-500/12 p-4 text-sm text-amber-100">
            Your Clerk account is missing the name details needed to prepare trial access. Add a first name or display name, then try again.
          </div>
        )}

        <section className="mt-6 overflow-hidden rounded-[28px] border border-[#1b2029] bg-[radial-gradient(circle_at_18%_18%,rgba(47,61,108,0.26),transparent_34%),radial-gradient(circle_at_82%_12%,rgba(130,101,54,0.2),transparent_30%),linear-gradient(180deg,#06090f,#0b1019)] px-4 py-8 sm:mt-8 sm:px-6 sm:py-12 lg:rounded-3xl lg:px-10 lg:py-16">
          <div className="max-w-6xl">
            <div className="flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-start lg:gap-10">
              <img
                src={agencyLogoSrc}
                alt="Cold Case Agency"
                className="h-28 w-28 object-cover drop-shadow-[0_18px_44px_rgba(0,0,0,0.45)] sm:h-36 sm:w-36 lg:h-44 lg:w-44"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#c9bb97] sm:text-xs sm:tracking-[0.34em]">Cold Case Agency</p>
                <h1 className="mt-3 max-w-5xl text-4xl font-black leading-[0.92] text-[#efede7] sm:mt-4 sm:text-5xl lg:text-7xl">
                  Your free case file
                  <br />
                  is waiting.
                </h1>
                <p className="mt-5 max-w-4xl text-base leading-relaxed text-[#b7b0a2] sm:mt-6 sm:text-xl lg:text-2xl">
                  Start by creating your account to land your free case file. Join the Agency when
                  you’re ready for monthly case files, weekly archive drops, case sharing with your
                  friends, and earned promotions.
                </p>
                <div className="mt-8 grid gap-4 lg:grid-cols-2">
                  <article className="rounded-[26px] border border-[#394761] bg-[linear-gradient(180deg,rgba(159,210,255,0.18),rgba(16,27,42,0.86))] p-5 sm:p-6">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#bcdcff]">Start Free</p>
                    <h2 className="mt-3 text-2xl font-black text-[#eff7ff] sm:text-3xl">
                      Create your account.
                      <br />
                      Get the free case.
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-[#d2dfef] sm:text-base">
                      No credit card required. Sign up, and automatically receive access to your
                      free case, and see how the Agency feels before you decide to join.
                    </p>
                    {isSignedIn ? (
                      <a
                        href="#free-trial"
                        className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#394761] bg-[#9fd2ff] px-7 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#0a1727] hover:bg-[#b6ddff] sm:w-auto"
                      >
                        Get The Free Case
                      </a>
                    ) : (
                      <SignUpButton afterSignUpUrl="/agency/dashboard">
                        <button className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#394761] bg-[#9fd2ff] px-7 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#0a1727] hover:bg-[#b6ddff] sm:w-auto">
                          Get The Free Case
                        </button>
                      </SignUpButton>
                    )}
                  </article>
                  <article className="rounded-[26px] border border-[#2f3440] bg-[linear-gradient(180deg,rgba(72,58,39,0.38),rgba(12,17,26,0.94))] p-5 sm:p-6">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#d8c59c]">Join Membership</p>
                    <h2 className="mt-3 text-2xl font-black text-[#efede7] sm:text-3xl">
                      Join the Agency.
                      <br />
                      Keep the cases coming.
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-[#c8c0b2] sm:text-base">
                      Agency Memberships include: monthly case file, weekly archive episodes, case
                      sharing, and rank progression. (cancel any time)
                    </p>
                    {digitalOption ? (
                      isSignedIn ? (
                        <button
                          onClick={() => startJoin(digitalOption)}
                          disabled={workingOption.length > 0 || !hasRequiredCheckoutNameFields}
                          className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-7 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                        >
                          {workingOption === digitalOption.id
                            ? "Preparing checkout..."
                            : `Join The Agency | ${digitalOption.price || "$6"} Founding Rate`}
                        </button>
                      ) : (
                        <SignUpButton afterSignUpUrl="/agency/join?plan=digital">
                          <button className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-7 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110 sm:w-auto">
                            Join The Agency | {digitalOption.price || "$6"} Founding Rate
                          </button>
                        </SignUpButton>
                      )
                    ) : null}
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="structure" className="mt-8 rounded-[28px] border border-[#1b2029] bg-[#070a11] px-4 py-10 sm:mt-10 sm:px-6 sm:py-14 lg:rounded-3xl lg:px-10">
          <p className="text-xs uppercase tracking-[0.34em] text-[#c9bb97]">How Membership Works</p>
          <h2 className="mt-4 text-3xl font-black leading-tight text-[#efede7] sm:mt-5 sm:text-4xl lg:text-5xl">
            One membership. New reasons to come back every week.
          </h2>
          <p className="mt-5 max-w-4xl text-lg leading-relaxed text-[#ada89c] sm:mt-6 sm:text-xl lg:text-2xl">
            Agency membership is built around clear ongoing value. New cases arrive monthly, the
            archive keeps unfolding weekly, and your detective record keeps growing as you solve.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                title: "Monthly Case Files",
                body: "Each paid cycle unlocks a new numbered case file, so membership keeps delivering fresh investigations instead of ending after one solve.",
              },
              {
                title: "Weekly Archive Drops",
                body: "Your archive investigation keeps moving with weekly episode drops, giving you a reason to return between monthly case releases.",
              },
              {
                title: "Case Sharing",
                body: "Active members can open a guest session on a case and bring friends into the investigation without handing over the whole account.",
              },
              {
                title: "Earned Promotions",
                body: "Solve more cases, rise in rank, and build a record that makes your membership feel like progress instead of passive access.",
              },
            ].map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-[#252a35] bg-[radial-gradient(circle_at_20%_0%,rgba(59,55,92,0.16),transparent_50%),#0a0d15] p-5 sm:p-6"
              >
                <h3 className="text-2xl font-bold text-[#efede7] sm:text-3xl">{item.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-[#a8a399] sm:text-lg lg:text-xl">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="membership" className="mt-8 rounded-[28px] border border-[#1b2029] bg-[#070a11] px-4 py-10 sm:mt-10 sm:px-6 sm:py-14 lg:rounded-3xl lg:px-10">
          <div className="mb-8 overflow-hidden rounded-[28px] border border-[#2c3342] bg-[radial-gradient(circle_at_top_left,rgba(135,173,255,0.12),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(190,146,72,0.14),transparent_32%),#0c111a] p-5 sm:p-7">
            <div className="max-w-3xl">
              <p className="text-xs uppercase tracking-[0.34em] text-[#c9bb97]">Agency Clearance Ladder</p>
              <h3 className="mt-3 text-2xl font-black text-[#efede7] sm:text-3xl">
                Rank is visible. Advancement is earned.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#a7a296] sm:text-base">
                Your first two Agency clearances are visible from the start. Higher bureau badges remain classified until your solve record earns the next promotion.
              </p>
            </div>
            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              {AGENCY_RANK_TEASER.map((rank) => (
                <article
                  key={rank.key}
                  className={`rounded-3xl border p-4 transition ${
                    rank.revealed
                      ? "border-[#3a4252] bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]"
                      : "border-[#242a36] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01))]"
                  }`}
                >
                  <div className="flex items-center gap-3 xl:flex-col xl:items-start">
                    <div className={`shrink-0 ${rank.revealed ? "" : "opacity-55 grayscale"}`}>
                      <RankBadge
                        rankKey={rank.key}
                        rankLabel={rank.label}
                        size={72}
                        className={`h-[72px] w-[72px] rounded-2xl border p-2 ${
                          rank.revealed
                            ? "border-[#4f5b72] bg-[#111827]"
                            : "border-[#2b3240] bg-[#0a0f17] blur-[0.6px]"
                        }`}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#efede7]">{rank.label}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[#9da6b7]">
                        {rank.status}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.34em] text-[#c9bb97]">Membership</p>
          <h2 className="mt-4 text-3xl font-black leading-tight text-[#efede7] sm:mt-5 sm:text-4xl lg:text-5xl">
            Join The Agency For Full Access
          </h2>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-[#aca79c] sm:text-xl">
            This is the paid membership path. Active Agency members secure monthly case files,
            weekly archive drops, and rank promotions at the current founder rate before pricing
            rises.
          </p>
          <div className="mx-auto mt-8 max-w-3xl rounded-[28px] border border-[#2a2e39] bg-[radial-gradient(circle_at_50%_0%,rgba(76,71,110,0.14),transparent_48%),#0b0f19] p-5 text-center sm:mt-9 sm:rounded-3xl sm:p-8 lg:p-10">
            <p className="text-[11px] uppercase tracking-[0.34em] text-[#c9bb97]">Founding Detective Rate</p>
            <p className="text-5xl font-light text-[#efede7] sm:text-6xl lg:text-7xl">
              {digitalOption?.price || "$6"}
              <span className="ml-2 text-2xl text-[#afa99d] sm:text-3xl">/month</span>
            </p>
            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-[#9fa6b5]">
              Permanent once claimed
            </p>
            <p className="mt-4 text-base text-[#8e887c]">
              Planned future standard rate: <span className="font-semibold text-[#d8d1c0]">$9/month</span>
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#b0ab9f] sm:mt-7 sm:text-xl lg:text-2xl">
              Start with Case File 1001 and archive thriller, Shroud of Darkness - episode 1. From
              there, membership keeps progressing with the next monthly case, each week receive the
              next episode of the archive. Cancel any time, and never lose access to your cases or
              archives.
            </p>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2 xl:grid-cols-4">
              {[
                "Case File 1001 on join",
                "Weekly Shroud unlocks while active",
                "Rank progression through solved cases",
                "Lead Case Sharing Sessions while an active member.",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-[#2b303b] bg-[#0c1019] px-4 py-4 text-sm font-medium text-[#d8d1c0]"
                >
                  {item}
                </div>
              ))}
            </div>
            {digitalOption ? (
              isSignedIn ? (
                <button
                  onClick={() => startJoin(digitalOption)}
                  disabled={workingOption.length > 0 || !hasRequiredCheckoutNameFields}
                  className="mt-8 inline-flex w-full items-center justify-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {workingOption === digitalOption.id ? "Preparing checkout..." : "Claim Founder Rate"}
                </button>
              ) : (
                <SignUpButton afterSignUpUrl="/agency/join?plan=digital">
                  <button className="mt-8 inline-flex w-full items-center justify-center rounded-full border border-[#5e5443] bg-gradient-to-b from-[#3a3327] to-[#26231f] px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#ece1c8] hover:brightness-110 sm:w-auto">
                    Join The Agency At Founder Rate
                  </button>
                </SignUpButton>
              )
            ) : null}
          </div>
        </section>

        <section id="free-trial" className="mt-8 rounded-[28px] border border-[#1b2029] bg-[#070a11] px-4 py-10 sm:mt-10 sm:px-6 sm:py-12 lg:rounded-3xl lg:px-10">
          <p className="text-xs uppercase tracking-[0.34em] text-[#c9bb97]">Free Trial</p>
          <h3 className="mt-4 text-3xl font-black text-[#efede7] sm:text-4xl">Create Your Account. Get The Free Case.</h3>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-[#a8a399] sm:text-xl">
            This is separate from paid Agency membership. Create your account to unlock a free case
            file and try the Agency format before deciding whether you want to join.
          </p>
          {isSignedIn ? (
            <>
              {!hasRequestedTrialKey ? (
                <button
                  onClick={() => requestFreeCase()}
                  disabled={sendingFreeCase || testingEmail || !hasRequiredTrialIdentity}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#394761] bg-[#9fd2ff] px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#0a1727] hover:bg-[#b6ddff] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  {sendingFreeCase ? "Sending..." : "Send My Free Case"}
                </button>
              ) : (
                <p className="mt-6 text-sm text-sky-100/90">
                  Your free case key has already been requested for this account.
                </p>
              )}
              {isAdmin && (
                <button
                  onClick={testEmailSetup}
                  disabled={sendingFreeCase || testingEmail}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-[#5d6c88] px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#b8c7e6] hover:bg-[#5d6c88]/20 disabled:cursor-not-allowed disabled:opacity-70 sm:ml-3 sm:mt-6 sm:w-auto"
                >
                  {testingEmail ? "Testing..." : "Test Email Setup"}
                </button>
              )}
            </>
          ) : (
            <SignUpButton afterSignUpUrl="/agency/dashboard">
              <button className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-[#394761] bg-[#9fd2ff] px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#0a1727] hover:bg-[#b6ddff] sm:w-auto">
                Create Account And Get Free Case
              </button>
            </SignUpButton>
          )}
        </section>

        {error && (
          <p className="mt-5 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </p>
        )}
        {freeCaseResult && (
          <p className="mt-5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
            {freeCaseResult}
          </p>
        )}
      </div>
    </section>
  );
};

export default ClubWelcomePage;
